import {
  PanelNames,
  WbDiffEditor,
  WbDisclosure,
  WbLogsViewer,
  WbMetadataTable,
  WbSpacer,
} from '@agilelab/plugin-wb-platform';
import { Content } from '@backstage/core-components';
import {
  Box,
  Paper,
  TablePagination,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import yaml from 'yaml';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { CodeComparisonSkeleton } from '../wizard-common/CodeComparisonSkeleton';
import {
  AdditionalStep,
  getReviewData,
  Logs,
  Step,
  truncateLargeArrays,
} from './utils';
import { memo, useCallback, useMemo, useState } from 'react';

const useReviewStyles = makeStyles(theme => ({
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  metadataContainer: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
  },
  arrayInfoBox: {
    flex: '1 1 0%',
  },
  expandButton: {
    marginLeft: theme.spacing(1),
    textTransform: 'none',
  },
}));

/**
 * Component to render a paginated array
 */
const PaginatedArrayDisplay = memo(
  ({ allItems, totalLength }: { allItems: any[]; totalLength: number }) => {
    const [arrayPage, setArrayPage] = useState(0);
    const [arrayRowsPerPage, setArrayRowsPerPage] = useState(5);

    const paginatedArrayItems = allItems.slice(
      arrayPage * arrayRowsPerPage,
      arrayPage * arrayRowsPerPage + arrayRowsPerPage,
    );

    const handleArrayPageChange = (
      _event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number,
    ) => {
      setArrayPage(newPage);
    };

    const handleArrayRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setArrayRowsPerPage(parseInt(event.target.value, 10));
      setArrayPage(0);
    };

    return (
      <Box display="flex" flexDirection="column" style={{ gap: '4px' }}>
        <div style={{ padding: '4px' }}>
          <Typography variant="caption" color="textSecondary">
            Showing {arrayPage * arrayRowsPerPage + 1} to{' '}
            {Math.min((arrayPage + 1) * arrayRowsPerPage, totalLength)} of{' '}
            {totalLength} items
          </Typography>
        </div>

        {paginatedArrayItems.map((item, idx) => (
          <Box key={idx} padding={1} bgcolor="grey.50">
            <WbMetadataTable metadata={{ value: item }} />
          </Box>
        ))}
        {totalLength > arrayRowsPerPage && (
          <Box display="flex" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={totalLength}
              page={arrayPage}
              onPageChange={handleArrayPageChange}
              rowsPerPage={arrayRowsPerPage}
              onRowsPerPageChange={handleArrayRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 20]}
            />
          </Box>
        )}
      </Box>
    );
  },
);

const ReviewContent = memo(
  ({ formState, steps }: { formState: any; steps: Step[] }) => {
    const configApi = useApi(configApiRef);
    const configuredRowsPerPage = configApi.getOptionalNumber(
      'mesh.builder.scaffolder.arrayFields.rowsPerPage',
    );
    const maxArrayItems =
      configuredRowsPerPage &&
      Number.isFinite(configuredRowsPerPage) &&
      configuredRowsPerPage > 0
        ? Math.floor(configuredRowsPerPage)
        : 5;

    const reviewData = useMemo(() => {
      const data = getReviewData(formState, steps);
      return truncateLargeArrays(data, maxArrayItems);
    }, [formState, steps, maxArrayItems]);

    const classes = useReviewStyles();

    const isTruncatedArray = useCallback((value: any) => {
      return (
        value &&
        typeof value === 'object' &&
        '__isArrayTruncated' in value &&
        value.__isArrayTruncated === true
      );
    }, []);

    // Build metadata tree recursively so nested truncated arrays also get pagination controls.
    const processedMetadata = useMemo(() => {
      const processNode = (node: any, path: string[] = []): any => {
        if (isTruncatedArray(node)) {
          const label = path[path.length - 1] ?? 'Array';
          const allItems = (node as any).__allItems;
          const totalLength = (node as any).__totalLength;

          const headerInfo = (
            <Typography variant="caption">
              Array with {totalLength} items
            </Typography>
          );

          return (
            <WbDisclosure
              title={label}
              headerInfo={headerInfo}
              cardStyle={{ flex: '1 1 0%' }}
            >
              <PaginatedArrayDisplay
                allItems={allItems}
                totalLength={totalLength}
              />
            </WbDisclosure>
          );
        }

        if (Array.isArray(node)) {
          return node.map((item, index) =>
            processNode(item, [...path, `${index}`]),
          );
        }

        if (node && typeof node === 'object') {
          const processedData: Record<string, any> = {};
          Object.entries(node).forEach(([key, value]) => {
            processedData[key] = processNode(value, [...path, key]);
          });
          return processedData;
        }

        return node;
      };

      return processNode(reviewData);
    }, [reviewData, isTruncatedArray]);

    return (
      <Content>
        <Paper
          style={{ backgroundColor: 'white', wordBreak: 'break-word' }}
          square
          elevation={0}
        >
          <Box className={classes.metadataContainer}>
            <WbMetadataTable metadata={processedMetadata} />
          </Box>
          <WbSpacer />
        </Paper>
      </Content>
    );
  },
);

export const reviewStep = (
  title: string,
  nextButtonText: string,
  formState: Record<string, any>,
  steps: Step[],
  handleFormReset: () => void,
  handleCreate: () => Promise<void>,
  stayOnStep?: boolean,
): AdditionalStep => ({
  title,
  component: <ReviewContent formState={formState} steps={steps} />,
  disableStepButtons: false,
  backButtonText: 'Back',
  nextButtonText: nextButtonText,
  analyticsEvent: 'create',
  resetButtonText: 'Reset',
  stayOnStep,
  // Set the additional step to 1, which is the processing step
  onSaveButtonClick: ({ setAdditionalStep }) => {
    setAdditionalStep(1);
  },
  resetAction: handleFormReset,
  nextAction: handleCreate,
});

export const processingStep = (
  logs: Logs,
  isProcessingComplete: boolean,
  className: string,
  taskFailed: boolean,
  nextButtonText: string = 'Next step',
): AdditionalStep => ({
  title: 'Processing',
  component: (
    <Box className={className}>
      <WbLogsViewer {...logs} />
    </Box>
  ),
  disableStepButtons: !isProcessingComplete,
  customDisableStepButtons: { disableBack: false },
  backButtonText: 'Back',
  nextButtonText: nextButtonText,
  taskFailed,
});

export const comparisonStep = (options: {
  originalData: Record<string, any> | undefined;
  modifiedData: Record<string, any> | undefined;
  isLoading: boolean;
  classes: ClassNameMap<'button' | 'saveButton' | 'comparisonPageHeader'>;
  additionalActions: JSX.Element[];
  leftPanelTitle?: string;
  rightPanelTitle?: string;
  header?: JSX.Element;
}): AdditionalStep => ({
  title: 'Preview and Save',
  component: (
    <Box>
      {options.header}
      <PanelNames
        leftPanelTitle={options.leftPanelTitle ?? 'Source'}
        rightPanelTitle={options.rightPanelTitle ?? 'Modified'}
      />

      {options.isLoading ? (
        <CodeComparisonSkeleton height="calc(100vh - 305px)" />
      ) : (
        <WbDiffEditor
          height="calc(100vh - 305px)"
          width="100%"
          theme="diffEditorWizard"
          language="yaml"
          original={yaml.stringify(options.originalData)}
          modified={yaml.stringify(options.modifiedData)}
          options={{
            selectOnLineNumbers: true,
            contextmenu: false,
            readOnly: true,
            diffCodeLens: false,
            originalEditable: false,
            glyphMargin: true,
            linkedEditing: false,
            minimap: {
              enabled: false,
            },
          }}
        />
      )}
    </Box>
  ),
  additionalActions: options.additionalActions,
  resetButtonText: 'Start over',
  disableStepButtons: false,
});
